import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";

const defaultItems = [
  {
    id: 1,
    title: "Mountain Trek",
    category: "Adventure",
    description: "Scale new heights and embrace the hiker's journey.",
    imageSrc:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&h=600&fit=crop",
  },
  {
    id: 2,
    title: "River Rafting",
    category: "Extreme",
    description: "Feel the adrenaline rush as you navigate the wild rapids.",
    imageSrc:
      "https://images.unsplash.com/photo-1533294455001-2bf7e07e1bb1?w=800&h=600&fit=crop",
  },
  {
    id: 3,
    title: "Forest Walk",
    category: "Nature",
    description: "Deep dive into the silence of the ancient woods.",
    imageSrc:
      "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&h=600&fit=crop",
  },
  {
    id: 4,
    title: "Azure Beach",
    category: "Paradise",
    description: "Unwind on the crystal clear shores of a tropical paradise.",
    imageSrc:
      "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&h=600&fit=crop",
  },
  {
    id: 5,
    title: "Spiritual Path",
    category: "Serenity",
    description: "Discover inner peace through ancient wisdom.",
    imageSrc:
      "https://images.unsplash.com/photo-1542640244-9dcdd71e7e1b?w=800&h=600&fit=crop",
  },
];

const DefaultCard = ({ item }) => (
  <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-white ring-1 ring-black/5">
    <div className="absolute inset-0">
      <img
        src={item.imageSrc}
        alt={item.title}
        className="w-full h-full object-cover"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
    </div>
    <div className="absolute top-5 left-5">
      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30">
        {item.category}
      </span>
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
      <h3 className="text-2xl font-bold tracking-tight">{item.title}</h3>
      <p className="mt-1.5 text-sm text-white/80 leading-relaxed">{item.description}</p>
    </div>
  </div>
);

const CarouselStacked = React.forwardRef(
  (
    {
      items = defaultItems,
      className,
      autoAdvance = false,
      intervalMs = 4000,
      renderCard,
      cardHeight = 440,
    },
    ref
  ) => {
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const advance = useCallback(() => {
      setDirection(1);
      setIndex((prev) => (prev + 1) % items.length);
    }, [items.length]);

    const retreat = useCallback(() => {
      setDirection(-1);
      setIndex((prev) => (prev - 1 + items.length) % items.length);
    }, [items.length]);

    React.useEffect(() => {
      if (!autoAdvance) return;
      const timer = setInterval(advance, intervalMs);
      return () => clearInterval(timer);
    }, [autoAdvance, intervalMs, advance]);

    const visibleCount = Math.min(4, items.length);
    const stack = [];
    for (let i = 0; i < visibleCount; i++) {
      const cardIndex = (index + i) % items.length;
      stack.push({ ...items[cardIndex], stackPosition: i, key: `${cardIndex}-${i}` });
    }

    const CardComponent = renderCard || DefaultCard;

    return (
      <div ref={ref} className={cn("w-full", className)}>
        <div
          className="relative mx-auto max-w-md select-none"
          style={{ height: cardHeight }}
        >
          <AnimatePresence initial={false} custom={direction}>
            {stack.map((card) => {
              const isTop = card.stackPosition === 0;
              const depth = card.stackPosition;

              return (
                <motion.div
                  key={card.key}
                  className="absolute inset-0"
                  initial={{
                    scale: 1 - depth * 0.05,
                    y: depth * 12,
                    rotate: depth * 2,
                    opacity: 1,
                    zIndex: 100 - depth,
                  }}
                  animate={{
                    scale: 1 - depth * 0.05,
                    y: depth * 12,
                    rotate: depth * 2,
                    opacity: 1,
                    zIndex: 100 - depth,
                  }}
                  exit={{
                    x: direction > 0 ? 300 : -300,
                    rotate: direction > 0 ? 30 : -30,
                    opacity: 0,
                    transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  drag={isTop ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.6}
                  onDragEnd={(_, info) => {
                    if (info.offset.x > 100 || info.velocity.x > 500) {
                      retreat();
                    } else if (info.offset.x < -100 || info.velocity.x < -500) {
                      advance();
                    }
                  }}
                  whileTap={isTop ? { cursor: "grabbing" } : {}}
                  style={{ cursor: isTop ? "grab" : "default" }}
                >
                  <CardComponent item={card} />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={retreat}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
            aria-label="Previous"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="flex items-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > index ? 1 : -1);
                  setIndex(i);
                }}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === index ? "w-6 bg-primary" : "w-1.5 bg-gray-300 hover:bg-gray-400"
                )}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={advance}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
            aria-label="Next"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    );
  }
);

CarouselStacked.displayName = "CarouselStacked";

export { CarouselStacked };
export default CarouselStacked;
